/** @file Sets an event key: an event fires at a frame, optionally overriding its int/float/string. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { defineCommand } from '../defineCommand';
import { animationDraft } from './animationDraft';
import { insertByFrame } from './insertByFrame';
import { sameJson } from './sameJson';

type Overrides = { int?: number; float?: number; string?: string };

/** The override fields that were given; absent ones fall back to the event's defaults. */
function overrides(input: Overrides): Overrides {
  return {
    ...(input.int === undefined ? {} : { int: input.int }),
    ...(input.float === undefined ? {} : { float: input.float }),
    ...(input.string === undefined ? {} : { string: input.string }),
  };
}

/** animation.setEventKey: the same event on the same frame is replaced (an identical key is a no-op); others stay. */
export const command = defineCommand({
  name: 'animation.setEventKey',
  description: 'Fire event `event` (name or id) at `frame` with optional int/float/string overrides; the same event on the same frame is replaced, other events on that frame stay.',
  input: z
    .object({
      animation: z.string().min(1),
      frame: prim.frame,
      event: z.string().min(1),
      int: z.number().int().exactOptional(),
      float: prim.finite.exactOptional(),
      string: z.string().exactOptional(),
    })
    .strict(),
  apply(draft, input, ctx) {
    const { anim } = animationDraft(draft, input.animation, ctx);
    const event = ctx.resolve('event', input.event);
    const key = { f: input.frame, event, ...overrides(input) };
    const at = anim.events.findIndex((e) => e.f === input.frame && e.event === event);
    if (at >= 0 && sameJson(anim.events[at], key)) return;
    // One element write for a replaced key, a push or one rebuilt list for a new one (R17-01).
    if (at >= 0) anim.events[at] = key;
    else {
      insertByFrame(anim.events, key, (next) => {
        anim.events = next;
      });
    }
    draft.meta.updatedAt = ctx.now;
  },
});
